/**
 * `HybridTransport` —— 三级回退传输（迁移自 `prototype/web/app.js#hybridSend`）。
 *
 * 决策顺序（ARCH §5.2）：
 *   ① WebRTC DataChannel 已直连 → 直发，不消耗中继流量
 *   ② 直连不可用 → `/meshlink/relay-message/1.0.0` 转发
 *      - 对方在线：实时投递
 *      - 对方离线：中继入邮箱（TTL 7 天），返回 `stored:true`
 *   ③ 中继也不可用 → 抛 `E_RELAY_UNAVAILABLE`，由上层 Messenger 保留 pending 队列
 *
 * `kind:'signal'` 信封不进业务回调，直接喂给 `WebRTCManager`（打洞信令）。
 */

import { encodeFrame } from '@shared/protocol';
import type { ChatEnvelope, ConnStatus, OnlinePeer, SendResult } from '@shared/types';
import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';

import type { ITransport } from './ITransport';
import { RelayConnectionManager, type RelayConnState, type RelayStatus } from './connection';
import { Libp2pBrowserPeer } from './peer';
import { MailboxPoller } from './mailbox';
import { WebRTCManager, type WebRTCSignal } from './webrtc';
import type { TrafficMeter } from './traffic';

const logger = createLogger('net:transport');

export interface HybridTransportOptions {
  /** 32B Ed25519 peer 域种子 → PeerID（与 T02 派生一致）。 */
  peerSeed: Uint8Array;
  /** 备选中继地址（主中继连不上时按顺序试）。 */
  relays?: string[];
  /** 流量计量（可选）。 */
  traffic?: TrafficMeter;
  /** 出站 WebRTC 信令：由上层（ChatService）包装成 `kind:'signal'` 信封后 `sendRaw`。 */
  onOutgoingSignal?: (to: string, signal: WebRTCSignal) => void;
  /** 中继就绪回调（上报可见性 / 订阅广场）。 */
  onRelayReady?: (relayAddr: string, selfPeerId: string) => void;
  /** 中继状态变化（供 store 状态灯）。 */
  onRelayStatus?: (status: RelayStatus, relayAddr: string | null) => void;
  /**
   * 拨号接缝（测试用）：默认走 `Libp2pBrowserPeer.connectRelay`。
   * 失败语义与文案不变：`connectRelay` 返回 false → 抛 `E_RELAY_UNAVAILABLE('拨号中继失败 <addr>')`。
   */
  dialRelay?: (addr: string) => Promise<void>;
}

/** 直连通道连接成功后，认为该对端可直连的宽限期（ms）。 */
const DIRECT_GRACE_MS = 60_000;

export class HybridTransport implements ITransport {
  private readonly peer: Libp2pBrowserPeer;
  private readonly webrtc: WebRTCManager;
  private readonly conn: RelayConnectionManager;
  private readonly readyWaiters: Array<(ok: boolean) => void> = [];
  private readonly poller: MailboxPoller;
  private readonly envelopeCbs: Array<(env: ChatEnvelope, from: string, source?: 'live' | 'mailbox') => void> = [];
  private readonly statusCbs: Array<(peerId: string, status: ConnStatus, latencyMs?: number) => void> = [];
  private readonly knownPeers = new Set<string>();
  private relayReady = false;
  private started = false;

  constructor(private readonly opts: HybridTransportOptions) {
    this.peer = new Libp2pBrowserPeer({
      peerSeed: opts.peerSeed,
      onEnvelope: (env, from, source) => this.handleIncoming(env, from, source),
      onConnectionChange: (kind, pid) => {
        if (kind === 'disconnected' && this.relayReady && pid === this.peer.getRelayPeerId()) {
          logger.warn('与中继的连接断开，准备重连');
          this.relayReady = false;
          void this.conn.reconnect();
        }
      },
    });

    this.webrtc = new WebRTCManager({
      sendSignal: (to, signal) => this.opts.onOutgoingSignal?.(to, signal),
      onMessage: (peerId, data) => {
        const env = data as ChatEnvelope;
        if (!env || typeof env !== 'object' || env.protocol !== 'meshlink/chat/1.0') return;
        this.recordDown(env);
        for (const cb of this.envelopeCbs) {
          try {
            cb(env, peerId);
          } catch (err) {
            logger.error('直连信封回调异常', err instanceof Error ? err.message : err);
          }
        }
      },
      onStatusChange: (peerId, status) => {
        const mapped: ConnStatus =
          status === 'connected' ? 'direct' : status === 'connecting' ? 'connecting' : 'offline';
        this.emitStatus(peerId, mapped);
      },
    });

    this.conn = new RelayConnectionManager({
      endpoints: [],
      connect: (endpoint) => this.dialRelay(endpoint.addr),
      onState: (state, info) => this.handleRelayState(state, info),
    });

    this.poller = new MailboxPoller({
      poll: () => this.peer.pollMailbox(),
      onResult: (count) => logger.info('邮箱补收完成', count),
    });
  }

  // ─────────────── ITransport ───────────────

  async start(relayAddr: string): Promise<void> {
    if (this.started) return;
    this.started = true;

    await this.peer.start();
    const relays = [relayAddr, ...(this.opts.relays ?? [])].filter(Boolean).map((a) => ({ addr: a }));
    this.conn.setEndpoints(relays);
    // 后台重连循环：永驻直到 stop()，连接结果通过 onState 回调驱动 UI 状态灯
    void this.conn.start();
  }

  async stop(): Promise<void> {
    this.started = false;
    this.relayReady = false;
    this.poller.stop();
    this.conn.stop();
    this.webrtc.closeAll();
    await this.peer.stop();
  }

  async send(to: string, env: ChatEnvelope): Promise<SendResult> {
    this.knownPeers.add(to);

    // ① 直连
    if (this.webrtc.isDirect(to)) {
      const t0 = Date.now();
      if (this.webrtc.send(to, env)) {
        this.recordUp(env);
        return { kind: 'direct', latencyMs: Date.now() - t0 };
      }
    }

    // ② 中继转发 / 离线邮箱
    if (!this.relayReady || !this.peer.getRelayPeerId()) {
      throw new MeshLinkError('E_RELAY_UNAVAILABLE');
    }
    try {
      const t0 = Date.now();
      const { stored, queued } = await this.peer.sendMessage(to, env);
      this.recordUp(env);
      return {
        kind: stored ? 'mailbox' : 'relay',
        stored,
        queued,
        latencyMs: Date.now() - t0,
      };
    } catch (err) {
      this.relayReady = false;
      void this.conn.reconnect();
      if (err instanceof MeshLinkError) throw err;
      throw new MeshLinkError('E_RELAY_UNAVAILABLE', undefined, err);
    }
  }

  /** 轻量发送：已读回执 / 信令，失败静默，不进 ACK 流程。 */
  sendRaw(to: string, env: ChatEnvelope): void {
    this.knownPeers.add(to);
    if (this.webrtc.isDirect(to) && this.webrtc.send(to, env)) {
      this.recordUp(env);
      return;
    }
    if (!this.relayReady || !this.peer.getRelayPeerId()) return;
    this.peer
      .sendMessage(to, env)
      .then(() => this.recordUp(env))
      .catch(() => {
        // 回执/信令丢了不影响主流程，不重试
      });
  }

  probe(peerId: string): ConnStatus {
    if (this.webrtc.isDirect(peerId)) return 'direct';
    if (this.webrtc.getStatus(peerId) === 'connecting') return 'connecting';
    if (this.relayReady) return 'relay';
    return 'offline';
  }

  async ensureDirect(peerId: string): Promise<boolean> {
    this.knownPeers.add(peerId);
    return this.webrtc.connect(peerId);
  }

  onEnvelope(cb: (env: ChatEnvelope, from: string, source?: 'live' | 'mailbox') => void): void {
    this.envelopeCbs.push(cb);
  }

  onStatus(cb: (peerId: string, status: ConnStatus, latencyMs?: number) => void): void {
    this.statusCbs.push(cb);
  }

  // ─────────────── 额外能力（供 RelayService / UI） ───────────────

  /** 自身 PeerID。 */
  get selfPeerId(): string {
    return this.peer.peerId;
  }

  /** 中继是否已就绪。 */
  get isRelayReady(): boolean {
    return this.relayReady;
  }

  /** 当前中继地址。 */
  get currentRelay(): string | null {
    return this.conn.activeAddr;
  }

  /** 已建立直连的对端。 */
  directPeers(): string[] {
    return this.webrtc.directPeers();
  }

  /** 主动拉一次邮箱。 */
  async pollMailboxNow(): Promise<number> {
    return this.poller.pollNow();
  }

  /** 目录 / 中继目录代理（供 RelayService 使用）。 */
  getPeerClient(): Libp2pBrowserPeer {
    return this.peer;
  }

  /** 注册广场实时新帖回调（供 FeedService 订阅）。 */
  setFeedCallback(cb: (posts: unknown) => void): void {
    this.peer.setFeedCallback(cb);
  }

  /** 手动切换中继（取列表里的下一个）。 */
  async switchRelay(): Promise<boolean> {
    this.relayReady = false;
    await this.conn.reconnect();
    return this.waitReady(8_000);
  }

  // ─────────────── 扫码互联：中继候选热追加（ARCH_OfflineQR_Mesh §4.4） ───────────────

  /**
   * 扫码互联：把「Host 的中继接入点」插入候选（默认首位）并立即（重）连接。
   * 幂等：同一 `addr` 重复调用不重复插入；已就绪且当前正是该 addr 时直接返回 `true`。
   * @returns 是否在 `timeoutMs` 内到达 relay ready。
   */
  async joinRelay(addr: string, opts: { prioritize?: boolean; timeoutMs?: number } = {}): Promise<boolean> {
    const prior = opts.prioritize ?? true;
    const timeoutMs = opts.timeoutMs ?? 8_000;
    if (!this.conn.listEndpoints().some((e) => e.addr === addr)) {
      this.conn.addEndpoint({ addr }, { prioritize: prior });
    }
    if (this.relayReady && this.conn.activeAddr === addr) return true;
    this.relayReady = false;
    await this.conn.restart().catch(() => undefined);
    return this.waitReady(timeoutMs);
  }

  /** 退出局域网中继：移除候选并回到原候选集（仅当它是当前活动中继时才重启）。 */
  async leaveRelay(addr: string): Promise<void> {
    if (!this.conn.removeEndpoint(addr)) return;
    if (this.conn.activeAddr !== addr) return;
    this.relayReady = false;
    await this.conn.restart().catch(() => undefined);
  }

  /** 当前候选快照（UI 展示「已接入的中继」列表）。 */
  listRelayCandidates(): readonly string[] {
    return this.conn.listEndpoints().map((e) => e.addr);
  }

  /**
   * 心跳：用**一次**目录查询同时得到在线列表与端到端往返时延（ms）。
   * 失败返回空列表 + null（与 `refreshOnline()` 的降级语义一致）。
   */
  async fetchOnlineWithLatency(): Promise<{ peers: OnlinePeer[]; latencyMs: number | null }> {
    if (!this.relayReady) return { peers: [], latencyMs: null };
    const t0 = Date.now();
    try {
      const res = await this.peer.getOnlinePeers();
      // `getOnlinePeers().peers` 与 `OnlinePeer`（{peerId,name,connectedAt}）结构一致。
      return { peers: res.peers as OnlinePeer[], latencyMs: Date.now() - t0 };
    } catch {
      return { peers: [], latencyMs: null };
    }
  }

  /** 心跳：用一次目录查询测端到端往返时延（ms），失败返回 null。 */
  async measureLatency(): Promise<number | null> {
    return (await this.fetchOnlineWithLatency()).latencyMs;
  }

  /** 关闭指定对端的直连（拉黑 / 删除好友）。 */
  closeDirect(peerId: string): void {
    this.webrtc.close(peerId);
  }

  // ─────────────── 内部 ───────────────

  private async dialRelay(addr: string): Promise<void> {
    const dial = this.opts.dialRelay ?? ((a: string) => this.dialRelayViaPeer(a));
    await dial(addr);
  }

  /** 默认拨号实现（生产路径）：`connectRelay` 失败语义与文案不变。 */
  private async dialRelayViaPeer(addr: string): Promise<void> {
    const ok = await this.peer.connectRelay(addr);
    if (!ok) throw new MeshLinkError('E_RELAY_UNAVAILABLE', `拨号中继失败 ${addr}`);
  }

  private async afterConnected(addr: string): Promise<void> {
    this.relayReady = true;
    this.conn.markReady();
    this.poller.start();
    // 订阅广场实时流（此前无任何调用点 → 实时新帖从未推送过）。
    // 放在这里而非 setFeedCallback：保证中继已就绪，且重连后会自动重订阅。
    this.peer.feedSubscribe();
    for (const peerId of this.knownPeers) this.emitStatus(peerId, this.probe(peerId));
    this.opts.onRelayReady?.(addr, this.peer.peerId);
    for (const w of this.readyWaiters.splice(0)) w(true);
  }

  /** 中继连接状态变化：驱动 UI 状态灯，并在首次连上时完成业务就绪。 */
  private handleRelayState(state: RelayConnState, info: { addr?: string; attempt: number; error?: string }): void {
    this.opts.onRelayStatus?.(state, info.addr ?? null);
    if (state === 'connected') {
      void this.afterConnected(info.addr ?? '');
    } else if (state === 'disconnected') {
      this.relayReady = false;
    }
  }

  /** 等待中继就绪（切换中继后用于返回 Promise 结果）。 */
  private waitReady(timeoutMs: number): Promise<boolean> {
    if (this.relayReady) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => resolve(this.relayReady), timeoutMs);
      this.readyWaiters.push(() => {
        clearTimeout(timer);
        resolve(true);
      });
    });
  }

  private handleIncoming(env: ChatEnvelope, from: string, source?: 'live' | 'mailbox'): void {
    this.recordDown(env);
    this.knownPeers.add(from);

    // 打洞信令不进业务回调
    if (env.kind === 'signal') {
      void this.webrtc.handleSignal(from, {
        type: (env.type ?? '') as WebRTCSignal['type'],
        sdp: env.sdp,
        candidate: env.candidate,
        sdpMid: env.sdpMid,
        sdpMLineIndex: env.sdpMLineIndex,
      });
      return;
    }

    for (const cb of this.envelopeCbs) {
      try {
        cb(env, from, source);
      } catch (err) {
        logger.error('信封回调异常', err instanceof Error ? err.message : err);
      }
    }
  }

  private emitStatus(peerId: string, status: ConnStatus, latencyMs?: number): void {
    for (const cb of this.statusCbs) cb(peerId, status, latencyMs);
  }

  private recordUp(env: ChatEnvelope): void {
    this.opts.traffic?.recordUp(frameBytes(env));
  }

  private recordDown(env: ChatEnvelope): void {
    this.opts.traffic?.recordDown(frameBytes(env));
  }
}

/** 信封的近似字节数（用于流量计量，与真实帧长误差 < 4B）。 */
function frameBytes(env: ChatEnvelope): number {
  try {
    return encodeFrame(env).length;
  } catch {
    return JSON.stringify(env).length;
  }
}

export { DIRECT_GRACE_MS };
export default HybridTransport;
